<template>
  <div>
    <ExternalIdFormDialog
      v-if="showExternalIdFormDialog"
      :visible="showExternalIdFormDialog"
      :gear-id="externalIdFormProps.gearId"
      :event-id="externalIdFormProps.eventId"
      :leg-id="externalIdFormProps.legId"
      :platform-id="externalIdFormProps.platformId"
      :person-id="externalIdFormProps.personId"
      :affiliation-id="externalIdFormProps.affiliationId"
      :project-id="externalIdFormProps.projectId"
      :external-id-id="externalIdFormProps.externalIdId"
      title="External ID"
      @saved="
        showExternalIdFormDialog = false;
        refresh();
      "
      @close="
        showExternalIdFormDialog = false;
        refresh();
      "
    >
    </ExternalIdFormDialog>
    <DeleteDialog
      v-if="showDeleteDialog"
      :visible="showDeleteDialog"
      @delete-confirmed="onDeleteConfirmed"
      @close="
        showDeleteDialog = false;
        notDeletedAlertText = '';
        refresh();
      "
      title="Delete External ID?"
      :warning="notDeletedAlertText"
    >
    </DeleteDialog>

    <v-data-table
      :headers="headers"
      :items="external_ids"
      :loading="loading"
      item-key="name"
      class="elevation-2"
      :footer-props="{
        'items-per-page-options': [10, 50, 100, -1],
        'show-first-last-page': true,
        'next-icon':$icons.mdiChevronRight,
        'last-icon':$icons.mdiChevronDoubleRight,
        'prev-icon':$icons.mdiChevronLeft,
        'first-icon':$icons.mdiChevronDoubleLeft
      }"
      :header-props="{
         'sort-icon': $icons.mdiChevronUp,
      }"
      :items-per-page.sync="ExternalIdsItemsPerPage"
    >
      <template v-slot:top>
        <v-container>
          <v-row>
            <v-spacer></v-spacer>
            <v-col>
              <v-btn
                v-if="postPermission"
                class="elevation-1 plus-button"
                fab
                x-small
                @click="addExternalId()"
              >
                <v-icon color="white"> {{$icons.mdiPlus}} </v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </template>
      <template v-slot:[`item.id_type`]="{ item }">
        {{ item.external_id_type }}
      </template>
      <template v-slot:[`item.external_id`]="{ item }">
        <a
          v-bind:href=item.web_ui_link
          target="_blank"
        >{{ item.external_id }}
        </a>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <v-row>
          <v-tooltip
            top
            v-if="
              editPermission
            "
            content-class="osis-tooltip"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                small
                class="osis-orange mr-2"
                @click="editExternalId(item)"
              >
                {{$icons.mdiPencil}}
              </v-icon>
            </template>
            <span>Edit</span>
          </v-tooltip>
          <v-icon
            v-else
            small
            color="grey"
            class="mr-2"
          >
            {{$icons.mdiPencil}}
          </v-icon>
          <v-tooltip
            top
            v-if="
              deletePermission
            "
            content-class="osis-tooltip"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                small
                class="osis-red mr-2"
                @click="deleteExternalId(item)"
              >
                {{$icons.mdiDeleteOutline}}
              </v-icon>
            </template>
            <span>Delete</span>
          </v-tooltip>
          <v-icon
            v-else
            small
            color="grey"
            class="mr-2"
          >
            {{$icons.mdiDeleteOutline}}
          </v-icon>
        </v-row>
      </template>

    </v-data-table>
  </div>
</template>

<script>
import apiRequests from '@/mixins/apiRequests'
import permissions from '@/mixins/permissions'
import helperFunctions from '@/mixins/helperFunctions'

export default {
  name: 'ExternalIds',
  mixins: [apiRequests, permissions, helperFunctions],
  components: {
    ExternalIdFormDialog: () => import('@/components/ExternalIds/ExternalIdFormDialog.vue'),
    DeleteDialog: () => import('@/components/DeleteDialog.vue'),
  },
  props: ['gearId', 'eventId', 'legId', 'platformId', 'affiliationId', 'personId'],
  data() {
    return {
      panel: [],
      external_ids: [],
      loading: true,
      loadPermissions: true,
      options: {},
      editPermission: false,
      postPermission: false,
      deletePermission: false,
      showExternalIdFormDialog: false,
      showDeleteDialog: false,
      selectedTypes: [],
      externalIdFormProps: {
        externalIdId: null,
        gearId: null,
        eventId: null,
        legId: null,
        platformId: null,
        affiliationId: null,
        personId: null,
        projectId: null,
      },
      idToDelete: null,
      notDeletedAlertText: '',
    }
  },
  computed: {
    headers() {
      if (this.eventId || this.gearId || this.platformId || this.affiliationId || this.personId || this.legId) {
        return [
          {
            text: 'External ID Type',
            value: 'id_type',
            sortable: false
          },
          {
            text: 'External ID',
            value: 'external_id',
          },
          {
            text: 'Actions',
            value: 'actions',
            sortable: false
          }
        ]
      } else {
        return [
          {
            text: 'Link',
            value: 'link_path',
          },
          {
            text: 'Description',
            value: 'description',
          },
          {
            text: 'Date created',
            value: 'date_created',
          },
          {
            text: 'Related to',
            value: 'related_to',
            sortable: false
          },
          {
            text: 'Actions',
            value: 'actions',
            sortable: false
          }
        ]
      }
    },
    ExternalIdsItemsPerPage: {
      get() {
        // Retrieve the value from the cookie, or use a default value
        return parseInt(this.$cookies.get("ExternalIdsItemsPerPage")) || 10;
      },
      set(value) {
        // Save the selected value to the cookie
        this.$cookies.set("ExternalIdsItemsPerPage", value);
      },
    },
  },
  watch: {
  },
  async mounted() {
    await this.getExternalIds()
    await this.getPermissions()
  },
  methods: {
    relatedToFilter(value) {
      if (!this.list) {
        return true;
      }
      return this.list.includes(value);
    },
    async getExternalIds() {
      this.loading = true
      let requestParams = {}
      if (this.gearId) {
        requestParams = {
          method: "GET",
          url: 'gears/' + this.gearId + '/external_ids'
        }
      } else if (this.platformId) {
        requestParams = {
          method: "GET",
          url: 'platforms/' + this.platformId + '/external_ids'
        }
      } else if (this.eventId) {
        requestParams = {
          method: "GET",
          url: 'expeditions/' + this.legId + '/events/' + this.eventId + '/external_ids'
        }
      } else if (this.affiliationId) {
        requestParams = {
          method: "GET",
          url: 'affiliations/' + this.affiliationId + '/external_ids'
        }
      } else if (this.personId) {
        requestParams = {
          method: "GET",
          url: 'persons/' + this.personId + '/external_ids'
        }
      } else if (this.legId) {
        requestParams = {
          method: "GET",
          url: 'expeditions/' + this.legId + '/external_ids'
        }
      }

      try {
        const res = await this.osisApiRequest(requestParams)
        this.external_ids = await res.json()
      } catch (error) {
        console.log(error)
      }
      this.loading = false
    },
    async getPermissions() {
      let baseUrl = ''
      let isMainObject = false
      if (this.legId) {
        isMainObject = true
        baseUrl = '/v1/expeditions/' + this.legId + '/external_ids'
      } else if (this.experimentId) {
        isMainObject = true
      } else if (this.simulationId) {
        isMainObject = true
      } else if (this.gearId) {
        baseUrl = '/v1/gears/' + this.gearId + '/external_ids'
      } else if (this.platformId) {
        baseUrl = '/v1/platforms/' + this.platformId + '/external_ids'
      } else if (this.affiliationId) {
        baseUrl = '/v1/affiliations/' + this.affiliationId + '/external_ids'
      } else if (this.personId) {
        baseUrl = '/v1/persons/' + this.personId + '/external_ids'
      }

      if (!isMainObject) {
        let permissionsToAsk = {
          'editPermission': { route: baseUrl + '/0', action: 'PATCH' },
          'postPermission': { route: baseUrl, action: 'POST' },
          'deletePermission': { route: baseUrl + '/0', action: 'DELETE' },
        }
        let permissions = await this.getRoutePermission(permissionsToAsk)
        this.editPermission = permissions.editPermission.permission
        this.postPermission = permissions.postPermission.permission
        this.deletePermission = permissions.deletePermission.permission
      } else {
        if (this.legId) {
          let permissionsToAsk = {
            'patchPermission': { leg_id: this.legId, action: 'patch' }
          }
          let permissions = await this.getExpeditionPermissionFromApi(permissionsToAsk)
          this.editPermission = permissions.patchPermission.permission
          this.postPermission = permissions.patchPermission.permission
          this.deletePermission = permissions.patchPermission.permission
        } else if (this.experimentId) {
          let permissionsToAsk = {
            'patchPermission': { experiment_id: this.experimentId, action: 'patch' }
          }
          let permissions = await this.getExperimentPermissionFromApi(permissionsToAsk)
          this.editPermission = permissions.patchPermission.permission
          this.postPermission = permissions.patchPermission.permission
          this.deletePermission = permissions.patchPermission.permission
        } else if (this.simulationId) {
          let permissionsToAsk = {
            'patchPermission': { simulation_id: this.simulationId, action: 'patch' }
          }
          let permissions = await this.getSimulationPermissionFromApi(permissionsToAsk)
          this.editPermission = permissions.patchPermission.permission
          this.postPermission = permissions.patchPermission.permission
          this.deletePermission = permissions.patchPermission.permission
        }
      }

    },
    async onDeleteConfirmed() {
      let url = 'external_ids/' + this.idToDelete

      const requestParams = {
        method: "DELETE",
        url: url,
      }
      try {
        const res = await this.osisApiRequest(requestParams)
        if (res.status == 204) {
          this.showDeleteDialog = false
          await this.refresh()
        }
        else {
          const data = await res.json()
          this.notSavedAlertText = this.extractFirstErrorMessage(data.message)
        }
      }
      catch (error) {
        console.log(error)
      }
    },
    editExternalId(externalId) {
      Object.keys(this.externalIdFormProps).forEach((i) => this.externalIdFormProps[i] = null)
      this.externalIdFormProps.externalIdId = externalId.id
      if (this.gearId) {
        this.externalIdFormProps.gearId = this.gearId
      } else if (this.platformId) {
        this.externalIdFormProps.platformId = this.platformId
      } else if (this.eventId) {
        this.externalIdFormProps.eventId = this.eventId
        this.externalIdFormProps.legId = this.legId
      } else if (this.legId) {
        this.externalIdFormProps.legId = this.legId
      } else if (this.affiliationId) {
        this.externalIdFormProps.affiliationId = this.affiliationId
      } else if (this.personId) {
        this.externalIdFormProps.personId = this.personId
      }
      this.showExternalIdFormDialog = true
    },
    addExternalId() {
      Object.keys(this.externalIdFormProps).forEach((i) => this.externalIdFormProps[i] = null)
      if (this.gearId) {
        this.externalIdFormProps.gearId = this.gearId
      } else if (this.platformId) {
        this.externalIdFormProps.platformId = this.platformId
      } else if (this.eventId) {
        this.externalIdFormProps.eventId = this.eventId
        this.externalIdFormProps.legId = this.legId
      } else if (this.legId) {
        this.externalIdFormProps.legId = this.legId
      } else if (this.affiliationId) {
        this.externalIdFormProps.affiliationId = this.affiliationId
      } else if (this.personId) {
        this.externalIdFormProps.personId = this.personId
      }
      this.showExternalIdFormDialog = true
    },
    deleteExternalId(externalId) {
      this.idToDelete = externalId.id
      this.showDeleteDialog = true
    },
    async refresh() {
      await this.getExternalIds()
      this.$emit('external-ids-refreshed')
    },
  }
}
</script>

<style>
</style>